import React from 'react'
import classNames from 'classnames'
import { ReferenceLine } from 'recharts'
import {
    dateFormatter,
    dateFormatter2,
    detailDateFormatter,
    chartDateFormatter4,
    chartDateFormatter5,
    chartDateFormatter6,
    chartDateFormatter7,
    chartDateFormatter8,
    chartDateFormatter9,
    chartDateFormatter10,
    chartDateFormatter11,
    chartDateFormatter12,
    chartDateFormatter13,
    chartDateFormatter14,
    chartDateFormatter15,
    chartDateFormatter16,
    chartDateFormatter17,
    chartDateFormatter18,
    chartDateFormatter19,
    truncate
} from 'utils/common';
import {
    INTERVAL_DAY,
    INTERVAL_MINUTE,
    INTERVAL_HOUR,
    INTERVAL_WEEK,
    INTERVAL_MONTH,
    INTERVAL_YEAR
} from '../chartConfigurations';
import {
    isOptionRelativeTime,
    getRelativeTimeDates
} from '../editor/inputs/DatePicker'
import numeral from 'numeral';
import { cloneDeep, isEmpty, has, sortBy } from 'lodash';
import {
    chartData,
    chartDataPreview
} from 'api/dashboard';
import { CHART_RENDER_RULESET, BLOCK, WARN } from './constants'
import { THEME_COLOR_SWATCH } from 'utils/constants'

 const hashStringToUInt32 = (str) => {
     const s = (str == null) ? '' : String(str)
     let hash = 2166136261
     for (let i = 0; i < s.length; i += 1) {
         hash ^= s.charCodeAt(i)
         hash = Math.imul(hash, 16777619)
     }
     return hash >>> 0
 }

 const hslToHex = (h, s, l) => {
     const sat = Math.max(0, Math.min(100, s)) / 100
     const lig = Math.max(0, Math.min(100, l)) / 100
     const c = (1 - Math.abs(2 * lig - 1)) * sat
     const hp = ((h % 360) + 360) % 360 / 60
     const x = c * (1 - Math.abs((hp % 2) - 1))
     let r1 = 0
     let g1 = 0
     let b1 = 0
     if (hp >= 0 && hp < 1) { r1 = c; g1 = x; b1 = 0 }
     else if (hp >= 1 && hp < 2) { r1 = x; g1 = c; b1 = 0 }
     else if (hp >= 2 && hp < 3) { r1 = 0; g1 = c; b1 = x }
     else if (hp >= 3 && hp < 4) { r1 = 0; g1 = x; b1 = c }
     else if (hp >= 4 && hp < 5) { r1 = x; g1 = 0; b1 = c }
     else { r1 = c; g1 = 0; b1 = x }
     const m = lig - c / 2
     const r = Math.round((r1 + m) * 255)
     const g = Math.round((g1 + m) * 255)
     const b = Math.round((b1 + m) * 255)
     const toHex = (n) => n.toString(16).padStart(2, '0')
     return `#${toHex(r)}${toHex(g)}${toHex(b)}`
 }

 const getUniquePalette = (palette) => {
     const src = Array.isArray(palette) ? palette : []
     const seen = new Set()
     const unique = []
     for (const c of src) {
         const key = String(c).toLowerCase()
         if (!seen.has(key)) {
             seen.add(key)
             unique.push(c)
         }
     }
     return unique
 }

 const getIndexBasedColor = (index, reservedColors = []) => {
     const reserved = new Set((Array.isArray(reservedColors) ? reservedColors : []).map((c) => String(c).toLowerCase()))
     const i = (Number.isFinite(index) ? index : 0)

     for (let attempt = 0; attempt < 24; attempt += 1) {
         const hue = (i * 137.508 + attempt * 17) % 360
         const sat = 68 + ((i + attempt) % 3) * 6
         const lig = 48 + ((i + attempt) % 2) * 8
         const color = hslToHex(hue, sat, lig)
         if (!reserved.has(String(color).toLowerCase())) {
             return color
         }
     }

     return hslToHex((i * 137.508) % 360, 75, 55)
 }

 export const getDeterministicRandomColor = (key, { reservedColors = [] } = {}) => {
     const reserved = new Set((Array.isArray(reservedColors) ? reservedColors : []).map((c) => String(c).toLowerCase()))
     const hash = hashStringToUInt32(key)
 
     const baseHue = hash % 360
     const baseSat = 60 + ((hash >>> 8) % 25)
     const baseLig = 42 + ((hash >>> 16) % 18)
 
     for (let attempt = 0; attempt < 12; attempt += 1) {
         const hue = (baseHue + attempt * 29) % 360
         const sat = Math.max(45, Math.min(90, baseSat + (attempt % 2 === 0 ? 0 : -8)))
         const lig = Math.max(35, Math.min(70, baseLig + (attempt % 3 === 0 ? 4 : 0)))
         const color = hslToHex(hue, sat, lig)
         if (!reserved.has(String(color).toLowerCase())) {
             return color
         }
     }
 
     return hslToHex((baseHue + 1) % 360, baseSat, baseLig)
 }

 export const getZigZagThemeColor = (index, palette = THEME_COLOR_SWATCH) => {
     const n = Array.isArray(palette) ? palette.length : 0
     if (!n) return ''
     const mappedIdx = (index % 2 === 0)
         ? Math.floor(index / 2)
         : (n - 1) - Math.floor((index - 1) / 2)
     return palette[mappedIdx % n]
 }

 export const getThemeThenRandomSeriesColor = ({ key, index, palette = THEME_COLOR_SWATCH, reservedColors = [] }) => {
     const uniquePalette = getUniquePalette(palette)
     const n = uniquePalette.length
     if (!n) return getDeterministicRandomColor(key)
     if (typeof index === 'number' && index >= 0 && index < n) {
         return uniquePalette[index]
     }
     return getIndexBasedColor(index - n, [...uniquePalette, ...reservedColors])
 }

export const DEFAULT_CARTESIAN_CHART_CONFIG = {
    title: '',
    legendPosition: 'below',
    chartLayout: 'stacked',
    format: 'Default',
    decimal: 0,
    yPrefix: '',
    ySuffix: '',
    yTitle: {
        enabled: false,
        value: ''
    },
    threshold: {
        enabled: false,
        name: '',
        type: '',
        value: 0,
        color: ''
    },
    yDataColors: [],
    xTitle: {
        enabled: false,
        value: ''
    },
    xGroupByColors: [],
    dateFormatter: 'dateFormatter',
    intervalQuantity: 0,
    yDataOverrides: {},
    yDataColorObj: {}
}

const DECIMAL_FORTMAT_MAP = {
    0: '0',
    1: '0.0',
    2: '0.00',
    3: '0.000',
    4: '0.0000',
    5: '0.00000',
    6: '0.000000',
    7: '0.0000000',
    8: '0.00000000'
}

export const formatDate = (date, formatterKey) => {
    const formatters = {
        "dateFormatter": (time) => dateFormatter(time, true),
        "dateFormatter2": (time) => dateFormatter2(time, false),
        "dateFormatter3": (time) => detailDateFormatter(time, true),
        'chartDateFormatter4': (time) => chartDateFormatter4(time),
        'chartDateFormatter5': (time) => chartDateFormatter5(time),
        'chartDateFormatter6': (time) => chartDateFormatter6(time),
        'chartDateFormatter7': (time) => chartDateFormatter7(time),
        'chartDateFormatter8': (time) => chartDateFormatter8(time),
        'chartDateFormatter9': (time) => chartDateFormatter9(time),
        'chartDateFormatter10': (time) => chartDateFormatter10(time),
        'chartDateFormatter11': (time) => chartDateFormatter11(time),
        'chartDateFormatter12': (time) => chartDateFormatter12(time),
        'chartDateFormatter13': (time) => chartDateFormatter13(time),
        'chartDateFormatter14': (time) => chartDateFormatter14(time),
        'chartDateFormatter15': (time) => chartDateFormatter15(time),
        'chartDateFormatter16': (time) => chartDateFormatter16(time),
        'chartDateFormatter17': (time) => chartDateFormatter17(time),
        'chartDateFormatter18': (time) => chartDateFormatter18(time),
        'chartDateFormatter19': (time) => chartDateFormatter19(time),
    }
    const selectedFormatter = formatters[formatterKey] || formatters["dateFormatter"];

    return selectedFormatter(date);
}

export const isValidTimeRange = (chartInterval, startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeRangeInDays = (end - start) / (1000 * 60 * 60 * 24);

    const intervalMaxAllowedDays = {
        [INTERVAL_MINUTE]: 1,
        [INTERVAL_HOUR]: 3,
        [INTERVAL_DAY]: Infinity,
        [INTERVAL_WEEK]: Infinity,
        [INTERVAL_MONTH]: Infinity,
        [INTERVAL_YEAR]: Infinity
    }

    return timeRangeInDays <= (intervalMaxAllowedDays[chartInterval] || 0);
}

export const isChartDataTimeSeries = (groupBy) => {
    return groupBy.lenght === 0 || groupBy.includes('time')
}

export const getCartesianChartConfig = (chartConfig) => {
    const configuration = cloneDeep(DEFAULT_CARTESIAN_CHART_CONFIG)

    const basicFields = chartConfig.basicFields
    const yAxisFields = chartConfig.dataFields
    const xAxisFields = chartConfig.xAxisFields
    const scopeFields = chartConfig.scopeFields

    // SET CONFIGURATION FOR BASIC INFO
    const chartTitle = basicFields.find(item => item.id === 'title');
    if (chartTitle) configuration.title = chartTitle.value

    const legend = basicFields.find(item => item.id === 'legend');
    if (legend && legend.selectedOption) configuration.legendPosition = legend.selectedOption.value

    // SET CONFIGURATION FOR Y-AXIS INFO
    const enableYTitle = yAxisFields.find(item => item.id === 'enableYAxisTitle');
    if (enableYTitle && enableYTitle.value) {
        configuration.yTitle.enabled = enableYTitle.value

        const yTitle = yAxisFields.find(item => item.id === 'yAxisTitle');
        if (yTitle) configuration.yTitle.value = yTitle.value
    }

    const chartLayout = yAxisFields.find(item => item.id === 'chartLayout')
    if (chartLayout && chartLayout.selectedOption) configuration.chartLayout = chartLayout.selectedOption.value

    const format = yAxisFields.find(item => item.id === 'format')
    if (format && format.selectedOption) configuration.format = format.selectedOption.value

    const decimal = yAxisFields.find(item => item.id === 'decimal')
    if (decimal && decimal.selectedOption) configuration.decimal = decimal.selectedOption.value

    const yPrefix = yAxisFields.find(item => item.id === 'prefix');
    if (yPrefix) configuration.yPrefix = yPrefix.value

    const ySuffix = yAxisFields.find(item => item.id === 'suffix');
    if (ySuffix) configuration.ySuffix = ySuffix.value

    const enableThreshold = yAxisFields.find(item => item.id === 'enableThreshold');
    if (enableThreshold && enableThreshold.value) {
        configuration.threshold.enabled = enableThreshold.value

        if (configuration.threshold.enabled) {
            const thresholdName = yAxisFields.find(item => item.id === "thresholdName");
            if (thresholdName) configuration.threshold.name = thresholdName.value

            const thresholdType = yAxisFields.find(item => item.id === "thresholdType");
            if (thresholdType && thresholdType.selectedOption) {
                configuration.threshold.type = thresholdType.selectedOption.value

                if (thresholdType.enableCustomConfigs) {
                    const thresholdConfig = thresholdType.customConfigs.find(item => item.id === configuration.threshold.type);
                    if (thresholdConfig) {
                        configuration.threshold.value = thresholdConfig.value
                        configuration.threshold.color = thresholdConfig.color
                    }
                }
            }
        }

    }

    const yData = yAxisFields.find(item => item.id === 'data');
    if (yData && yData.enableCustomConfigs) {
        const yDataColors = yData.customConfigs.map(item => item.color) || []
        configuration.yDataColors = yDataColors

        const yDataOverrides = {}
        const yDataColorObj = {}
        yData.customConfigs.forEach(item => {
            yDataOverrides[item.id] = item.value
            yDataColorObj[item.id] = item.color
        })
        configuration.yDataOverrides = yDataOverrides
        configuration.yDataColorObj = yDataColorObj
    }

    // SET CONFIGURATION FOR X-AXIS INFO
    const enableXTitle = xAxisFields.find(item => item.id === 'enableXAxisTitle');
    if (enableXTitle && enableXTitle.value) {
        configuration.xTitle.enabled = enableXTitle.value

        const xTitle = xAxisFields.find(item => item.id === 'xAxisTitle');
        if (xTitle) configuration.xTitle.value = xTitle.value
    }

    const dateFormatter = xAxisFields.find(item => item.id === 'timeFormat');
    if (dateFormatter && dateFormatter.selectedOption) configuration.dateFormatter = dateFormatter.selectedOption.value

    const xGroupBy = xAxisFields.find(item => item.id === 'groupBy');
    if (xGroupBy && xGroupBy.enableCustomConfigs) {
        const xGroupByColors = xGroupBy.customConfigs.map(item => item.color) || []
        configuration.xGroupByColors = xGroupByColors
    }


    // SET CONFIGURATION FOR SCOPE INFO
    const intervalQuantity = scopeFields.find(item => item.id === 'intervalQuantity');
    if (intervalQuantity) configuration.intervalQuantity = intervalQuantity.value

    return configuration;
}

export const DEFAULT_TABLE_CHART_CONFIG = {
    title: '',
    metricConfigs: {},
    defaultMetricConfig: {
        metric: '',
        columnTitle: '',
        decimal: 0,
        prefix: '',
        suffix: '',
        coloring: 'background',
        thresholdType: 'percent',
        colorTiers: {
            total: 0,
            tiers: {
                0: {
                    active: false,
                    value: 0,
                    color: ''
                },
                1: {
                    active: false,
                    value: 0,
                    color: ''
                },
                2: {
                    active: false,
                    value: 0,
                    color: ''
                }
            }
        }
    },
    isRawData: false,
    interval: {
        enabled: false,
        format: 'dateFormatter2',
        title: ''
    },
}

export const getTableChartConfig = (chartConfig) => {
    const configuration = cloneDeep(DEFAULT_TABLE_CHART_CONFIG)

    const basicFields = chartConfig.basicFields
    const dataFields = chartConfig.dataFields
    const scopeFields = chartConfig.scopeFields

    // SET CONFIGURATION FOR BASIC FIELDS
    const chartTitle = basicFields.find(item => item.id === 'title');
    if (chartTitle) configuration.title = chartTitle.value

    // SET CONFIGURATION FOR METRICS CONFIG FIELDS
    const dataConfigs = dataFields.find(item => item.id === 'dataCallapsibleConfigs')
    if (dataConfigs && dataConfigs.dataConfigs && dataConfigs.dataConfigs.length > 0) {
        dataConfigs.dataConfigs.forEach(data => {
            const updatedMetricConfig = cloneDeep(configuration.defaultMetricConfig)
            const metricFields = data.fields

            const columnTitle = metricFields.find(item => item.id === 'title');
            if (columnTitle) updatedMetricConfig.columnTitle = columnTitle.value

            const decimal = metricFields.find(item => item.id === 'decimal')
            if (decimal && decimal.selectedOption) updatedMetricConfig.decimal = decimal.selectedOption.value

            const prefix = metricFields.find(item => item.id === 'prefix')
            if (prefix) updatedMetricConfig.prefix = prefix.value

            const suffix = metricFields.find(item => item.id === 'suffix')
            if (suffix) updatedMetricConfig.suffix = suffix.value

            const coloring = metricFields.find(item => item.id === 'coloring')
            if (coloring) updatedMetricConfig.coloring = coloring.selectedOption.value

            const thresholdType = metricFields.find(item => item.id === 'thresholdType')
            if (thresholdType && thresholdType.selectedOption) updatedMetricConfig.thresholdType = thresholdType.selectedOption.value

            const colorTiers = metricFields.find(item => item.id === 'colorTiers')
            if (colorTiers && colorTiers.selectedOption) updatedMetricConfig.colorTiers.total = colorTiers.selectedOption.value
            if (colorTiers.enableCustomConfigs && colorTiers.customConfigs.length > 0) {
                colorTiers.customConfigs.forEach(colorTier => {
                    if (updatedMetricConfig.colorTiers.tiers[colorTier.id]) {
                        updatedMetricConfig.colorTiers.tiers[colorTier.id].active = true
                        updatedMetricConfig.colorTiers.tiers[colorTier.id].value = colorTier.value
                        updatedMetricConfig.colorTiers.tiers[colorTier.id].color = colorTier.color
                    }
                })
            }
            configuration.metricConfigs[data.metric] = updatedMetricConfig
            configuration.metricConfigs[data.metric].metric = data.metric
        })
    }

    // SET CONFIGURATION FOR SCOPE INFO
    const columnMetricDataField = scopeFields.find(item => item.id === 'columnMetricData');
    if (columnMetricDataField && columnMetricDataField.selectedOption) {
        configuration.isRawData = columnMetricDataField.selectedOption.value === 'raw'
        if (!configuration.isRawData) {
            configuration.interval.enabled = true

            const intervalFormat = scopeFields.find(item => item.id === 'intervalFormat');
            if (intervalFormat && intervalFormat.selectedOption) configuration.interval.format = intervalFormat.selectedOption.value

            const intervalTitle = scopeFields.find(item => item.id === 'intervalTitle');
            if (intervalTitle) configuration.interval.title = intervalTitle.value.trim()
        }
    }

    return configuration;
}

export const groupByKeys = (data, keys) => {
    if (keys.length === 0) return data;

    const [key, ...remainingKeys] = keys;
    const grouped = {};

    data.forEach(item => {
        const groupValue = item.group[key];

        if (!grouped[groupValue]) {
            grouped[groupValue] = [];
        }
        grouped[groupValue].push(item);
    });

    Object.keys(grouped).forEach(groupValue => {
        grouped[groupValue] = groupByKeys(grouped[groupValue], remainingKeys);
    });

    return grouped;
}

export const groupCartesianData = (data, groupByKeys, currentGroupLevel = 0) => {
    if (currentGroupLevel >= groupByKeys.length) return data;

    const currentKey = groupByKeys[currentGroupLevel];
    const group = {};

    data.forEach(element => {
        const groupValue = element.group[currentKey]

        if (!group[groupValue]) {
            group[groupValue] = {
                [currentKey]: groupValue,
                groups: []
            }
        }

        if (currentGroupLevel === groupByKeys.length - 1) {
            group[groupValue].groups.push({
                values: element.values
            })
        } else {
            group[groupValue].groups.push(element)
        }
    });

    if (currentGroupLevel < groupByKeys.length - 1) {
        Object.keys(group).forEach(key => {
            group[key].groups = this.groupData(group[key].groups, groupByKeys, currentGroupLevel + 1)
        })
    }

    return Object.values(group)
}

export const customFormatDistance = (distance) => {
    return distance.replace('about', 'Last');
}

export const generateRandomValue = (arrayLength) => {
    if (arrayLength === 1) {
        return [100]; // Special case for single element arrays
    }

    if (arrayLength === 2) {
        return [0, 50]
    }

    if (arrayLength === 3) {
        return [0, 25, 75]
    }

    if (arrayLength === 4) {
        return [0, 20, 40, 60]
    }
}

export const getRandomHex = (list) => {
    if (list.length < 7) {
        return colorList[list.length]
    }
    return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
}

export const getCustomColorLabel = {
    0: 'Min',
    1: 'Threshold 1',
    2: 'Threshold 2',
    3: 'Threshold 3',
}

export const formatYAxisLabel = (value, format, decimal) => {
    if (value === null || value === undefined) return ''; // Handle null or undefined values

    const isNumericDecimal = typeof decimal === 'number' && !isNaN(decimal)
    const decimalFormat = isNumericDecimal ? (DECIMAL_FORTMAT_MAP[decimal] || '') : '0.[00000000]'

    const adjustedFormat = isNumericDecimal
        ? (Number(decimal) === 0 ? '0' : decimalFormat)
        : decimalFormat

    if (typeof value === 'string') {
        return value
    }

    if (decimal === 'fromData') {
        return String(value)
    }

    if (format === 'percent' || format === '%') {
        return `${numeral(value).format(decimalFormat)}`;
    }

    switch (format) {
        case '10':
            return numeral(value / 10).format(adjustedFormat) + ' (×10)'; // Tens
        case '100':
            return numeral(value / 100).format(adjustedFormat) + ' (×100)'; // Hundreds
        case '1000':
            return numeral(value / 1000).format(adjustedFormat);
        case '1000000':
            return numeral(value / 1000000).format(adjustedFormat); // Millions
        case '1000000000':
            return numeral(value / 1000000000).format(adjustedFormat) + 'Bil'; // Billions
        default:
            // Default and unknown formats: show full number without k/m/b abbreviation
            return numeral(value).format(adjustedFormat);
    }
};

export const normalizePercentData = (data = [], isStacked = false) => {
    if (!Array.isArray(data) || data.length === 0) return data;

    const toNum = (v) => (v == null || isNaN(v) ? 0 : Number(v));

    let denomGetter;
    if (isStacked) {
        // per bucket denominator
        denomGetter = (point) => {
            let sum = 0;
            if (point.values) sum += Object.values(point.values).reduce((a, v) => a + toNum(v), 0);
            Object.entries(point).forEach(([k, v]) => {
                if (!['bucketed_time', 'time', 'values'].includes(k)) sum += toNum(v);
            });
            return sum || 1;
        };
    } else {
        // global max denominator
        let globalMax = 0;
        data.forEach(p => {
            if (p.values) Object.values(p.values).forEach(v => globalMax = Math.max(globalMax, toNum(v)));
            Object.entries(p).forEach(([k, v]) => {
                if (!['bucketed_time', 'time', 'values'].includes(k)) globalMax = Math.max(globalMax, toNum(v));
            });
        });
        denomGetter = () => globalMax || 1;
    }

    return data.map(point => {
        const p = { ...point };
        const denom = denomGetter(point);

        if (p.values) {
            p.values = Object.fromEntries(
                Object.entries(p.values).map(([k, v]) => [k, (toNum(v) / denom) * 100])
            );
        }
        Object.entries(p).forEach(([k, v]) => {
            if (!['bucketed_time', 'time', 'values'].includes(k)) {
                p[k] = (toNum(v) / denom) * 100;
            }
        });
        return p;
    });
};

export const normalizePercentPieData = (data = []) => {
    if (!Array.isArray(data) || data.length === 0) return data;

    const total = data.reduce((sum, item) => sum + safeParseNumber(item?.value), 0);
    if (!total) return data;

    return data.map(item => ({
        ...item,
        value: (safeParseNumber(item?.value) / total) * 100
    }));
};

// Compute a trendline segment connecting the first and last points for a given series.
// Returns an array like [{ x, y }, { x, y }] or null when not possible.
export const getFirstLastTrendSegment = (processedChartData, seriesKey) => {
    if (!Array.isArray(processedChartData) || processedChartData.length < 2) return null;
    const first = processedChartData[0];
    const last = processedChartData[processedChartData.length - 1];
    const x1 = first?.bucketed_time;
    const x2 = last?.bucketed_time;
    const y1 = Number(first?.values?.[seriesKey]);
    const y2 = Number(last?.values?.[seriesKey]);
    if (x1 == null || x2 == null || isNaN(y1) || isNaN(y2)) return null;
    return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
}

//compute a trendline segment by performing a linear regression on the data points
export const getLinearRegressionTrendSegment = (processedChartData, seriesKey, getY) => {
    if (!Array.isArray(processedChartData) || processedChartData.length < 2) return null;

    const points = processedChartData
        .map((p, idx) => {
            const xRaw = p.bucketed_time ?? p.time;
            const parsed = xRaw != null ? Date.parse(xRaw) : NaN;
            const xNum = Number.isFinite(parsed) ? parsed : idx;
            const baseY = typeof getY === 'function' ? getY(p, idx) : p?.values?.[seriesKey];
            const y = Number(baseY);
            return { xRaw, xNum, y };
        })
        .filter(p => Number.isFinite(p.xNum) && Number.isFinite(p.y));

    const n = points.length;
    if (n < 2) return null;

    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (const { xNum: x, y } of points) {
        sumX += x;
        sumY += y;
        sumXY += x * y;
        sumXX += x * x;
    }

    const denom = n * sumXX - sumX * sumX;
    if (denom === 0) return null;

    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;

    const first = points[0];
    const last = points[points.length - 1];
    const y1 = slope * first.xNum + intercept;
    const y2 = slope * last.xNum + intercept;

    if (![y1, y2].every(Number.isFinite)) return null;
    return [{ x: first.xRaw, y: y1 }, { x: last.xRaw, y: y2 }];
}

export const renderSeriesTrendlines = (chartConfig, chartData, processedChartData, getStrokeLineCb) => {
    const streams = chartConfig?.dataStreams?.streams
    const series = chartData?.uniqueGroupValues
    if (!Array.isArray(streams) || !Array.isArray(series)) return null
    const isStacked = chartConfig?.chartLayout === 'stacked'
    const toNum = (v) => (v == null || isNaN(v) ? 0 : Number(v))
    return series.map((name, idx) => {
        const stream = streams[idx] || {}
        if (!stream.enableTrendline) return null
        let seg
        if (isStacked) {
            const getY = (point) => {
                const yCur = toNum(point?.values?.[name])
                const upto = series.slice(0, idx + 1)
                if (yCur >= 0) {
                    let s = 0
                    for (const k of upto) {
                        const v = toNum(point?.values?.[k])
                        if (v >= 0) s += v
                    }
                    return s
                } else {
                    let s = 0
                    for (const k of upto) {
                        const v = toNum(point?.values?.[k])
                        if (v < 0) s += v
                    }
                    return s
                }
            }
            seg = getLinearRegressionTrendSegment(processedChartData, name, getY)
        } else {
            seg = getLinearRegressionTrendSegment(processedChartData, name)
        }
        if (!seg) return null
        const stroke = typeof getStrokeLineCb === 'function' ? getStrokeLineCb(name, idx) : undefined
        return (
            <ReferenceLine key={`trend-${idx}`} segment={seg} stroke={stroke} strokeDasharray="3 3" />
        )
    })
}

export const getRoundedDecimalData = (value, decimal = 2) => {
    if (isNaN(value)) return null;

    // Ensure decimal is a finite number; fallback to 2 when non-numeric (e.g. 'fromData')
    const numericDecimal = Number.isFinite(Number(decimal)) ? Number(decimal) : 2;

    const factor = Math.pow(10, numericDecimal);
    // For 0 decimal places, round to the nearest whole number
    if (numericDecimal === 0) {
        return Math.round(value);
    }

    const truncated = Math.floor(value * factor) / factor;

    return truncated;
}

export const roundData = (data, decimal) => {
    const isNumericDecimal = typeof decimal === 'number' && !isNaN(decimal)
    if (data && isNumericDecimal) {
        return data.map(item => {
            const newItem = { ...item }

            // Round values in the 'values' object
            if (newItem.values && typeof newItem.values === 'object') {
                newItem.values = Object.fromEntries(
                    Object.entries(newItem.values).map(([key, value]) => [
                        key,
                        Number(getRoundedDecimalData(value, decimal)),
                    ])
                )
            }

            // Round direct stream properties (stream_1, stream_2, etc.)
            Object.keys(newItem).forEach(key => {
                if (key.startsWith('stream_') && typeof newItem[key] === 'number') {
                    newItem[key] = Number(getRoundedDecimalData(newItem[key], decimal))
                }
            })

            return newItem
        })
    }

    return data
}

export const getThresholdCalculatedValue = (thresholdConfig, yData, chartLayout, isUniqueGroupKeys = false, groupByKeys = []) => {
    if (thresholdConfig.type === 'absolute') {
        return numeral(thresholdConfig.value).value()
    }

    if (thresholdConfig.type === 'percent') {
        let allValues = []
        const validKeys = groupByKeys ? Object.values(groupByKeys) : []

        if (chartLayout === 'stacked') {
            // For stacked mode, sum values at each time point
            allValues = yData.map(entry => {
                let sum = 0

                // Check if entry has values property (new structure)
                if (entry.values && typeof entry.values === 'object') {
                    Object.entries(entry.values).forEach(([key, value]) => {
                        if (isUniqueGroupKeys && !isEmpty(validKeys)) {
                            if (validKeys.includes(key)) {
                                sum += safeParseNumber(value) || 0
                            }
                        } else {
                            sum += safeParseNumber(value) || 0
                        }
                    })
                } else {
                    // Fallback to direct properties (old structure)
                    Object.entries(entry).forEach(([key, value]) => {
                        if (key !== 'bucketed_time' && key !== 'time') {
                            if (isUniqueGroupKeys && !isEmpty(validKeys)) {
                                if (validKeys.includes(key)) {
                                    sum += safeParseNumber(value) || 0
                                }
                            } else {
                                sum += safeParseNumber(value) || 0
                            }
                        }
                    })
                }

                return sum
            })
        } else {
            // For non-stacked mode, collect all individual values
            allValues = yData.flatMap(entry => {
                const values = []

                // Check if entry has values property (new structure)
                if (entry.values && typeof entry.values === 'object') {
                    Object.entries(entry.values).forEach(([key, value]) => {
                        if (isUniqueGroupKeys && Array.isArray(validKeys) && validKeys.length > 0) {
                            if (validKeys.includes(key)) {
                                values.push(safeParseNumber(value) || 0)
                            }
                        } else {
                            values.push(safeParseNumber(value) || 0)
                        }
                    })
                } else {
                    // Fallback to direct properties (old structure)
                    Object.entries(entry).forEach(([key, value]) => {
                        if (key !== 'bucketed_time' && key !== 'time') {
                            if (isUniqueGroupKeys && Array.isArray(validKeys) && validKeys.length > 0) {
                                if (validKeys.includes(key)) {
                                    values.push(safeParseNumber(value) || 0)
                                }
                            } else {
                                values.push(safeParseNumber(value) || 0)
                            }
                        }
                    })
                }

                return values
            }).filter(value => value != null && !isNaN(value))
        }

        if (allValues.length === 0) {
            return 0
        }

        const percent = numeral(thresholdConfig.value).value()
        const sortedValues = allValues.sort((a, b) => a - b)
        const maxValue = sortedValues[sortedValues.length - 1]
        const percentValue = safeParseNumber((percent / 100) * maxValue)

        return percentValue || 0
    }

    return 0
}

export const DEFAULT_MAP_CHART_CONFIG = {
    title: '',
    dataOverrides: {},
    mapType: 'roadmap',
    format: 'Default',
    decimal: 0,
    prefix: '',
    suffix: '',
    enableMapLabels: false,
    thresholdType: 'percent',
    colorTiers: {
        total: 0,
        tiers: {
            0: {
                active: false,
                value: 0,
                color: ''
            },
            1: {
                active: false,
                value: 0,
                color: ''
            },
            2: {
                active: false,
                value: 0,
                color: ''
            }
        }
    }
}

export const DEFAULT_CARD_CHART_CONFIG = {
    title: '',
    dataOverrides: {},
    cardType: 'filledCard',
    format: 'Default',
    decimal: 0,
    prefix: '',
    suffix: '',
    thresholdType: 'percent',
    colorTiers: {
        total: 0,
        tiers: {
            0: {
                active: false,
                value: 0,
                color: ''
            },
            1: {
                active: false,
                value: 0,
                color: ''
            },
            2: {
                active: false,
                value: 0,
                color: ''
            }
        }
    }
}

export const getCardChartConfig = (chartConfig) => {
    const configuration = cloneDeep(DEFAULT_CARD_CHART_CONFIG)

    const basicFields = chartConfig.basicFields
    const dataFields = chartConfig.dataFields
    const scopeFields = chartConfig.scopeFields

    const chartTitle = basicFields.find(item => item.id === 'title')
    if (chartTitle) configuration.title = chartTitle.value

    const data = dataFields.find(item => item.id === 'data')
    if (data && data.enableCustomConfigs) {
        data.customConfigs.forEach(override => {
            configuration.dataOverrides[override.id] = override
        })
    }

    const cardType = dataFields.find(item => item.id === 'cardType')
    if (cardType && cardType.selectedOption) configuration.cardType = cardType.selectedOption.value

    const format = dataFields.find(item => item.id === 'format')
    if (format && format.selectedOption) configuration.format = format.selectedOption.value

    const decimal = dataFields.find(item => item.id === 'decimal')
    if (decimal && decimal.selectedOption) configuration.decimal = decimal.selectedOption.value

    const prefix = dataFields.find(item => item.id === 'prefix')
    if (prefix) configuration.prefix = prefix.value

    const suffix = dataFields.find(item => item.id === 'suffix')
    if (suffix) configuration.suffix = suffix.value

    const thresholdType = dataFields.find(item => item.id === 'thresholdType')
    if (thresholdType && thresholdType.selectedOption) configuration.thresholdType = thresholdType.selectedOption.value

    const colorTiers = dataFields.find(item => item.id === 'colorTiers')
    if (colorTiers && colorTiers.selectedOption) configuration.colorTiers.total = colorTiers.selectedOption.value
    if (colorTiers.enableCustomConfigs && colorTiers.customConfigs.length > 0) {
        colorTiers.customConfigs.forEach(colorTier => {
            if (configuration.colorTiers.tiers[colorTier.id]) {
                configuration.colorTiers.tiers[colorTier.id].active = true
                configuration.colorTiers.tiers[colorTier.id].value = colorTier.value
                configuration.colorTiers.tiers[colorTier.id].color = colorTier.color
            }
        })
    }

    return configuration
}

export const getMapChartConfig = (chartConfig) => {
    const configuration = cloneDeep(DEFAULT_MAP_CHART_CONFIG)

    const basicFields = chartConfig.basicFields
    const dataFields = chartConfig.dataFields

    const chartTitle = basicFields.find(item => item.id === 'title')
    if (chartTitle) configuration.title = chartTitle.value

    const data = dataFields.find(item => item.id === 'data')
    if (data && data.enableCustomConfigs) {
        data.customConfigs.forEach(override => {
            configuration.dataOverrides[override.id] = override
        })
    }

    const mapType = dataFields.find(item => item.id === 'mapType')
    if (mapType && mapType.selectedOption) configuration.mapType = mapType.selectedOption.value

    const format = dataFields.find(item => item.id === 'format')
    if (format && format.selectedOption) configuration.format = format.selectedOption.value

    const decimal = dataFields.find(item => item.id === 'decimal')
    if (decimal && decimal.selectedOption) configuration.decimal = decimal.selectedOption.value

    const prefix = dataFields.find(item => item.id === 'prefix')
    if (prefix) configuration.prefix = prefix.value

    const suffix = dataFields.find(item => item.id === 'suffix')
    if (suffix) configuration.suffix = suffix.value

    const enableMapLabels = dataFields.find(item => item.id === 'enableMapLabels')
    if (enableMapLabels && enableMapLabels.selectedOption) configuration.enableMapLabels = enableMapLabels.selectedOption.value

    const thresholdType = dataFields.find(item => item.id === 'thresholdType')
    if (thresholdType && thresholdType.selectedOption) configuration.thresholdType = thresholdType.selectedOption.value

    const colorTiers = dataFields.find(item => item.id === 'colorTiers')
    if (colorTiers && colorTiers.selectedOption) configuration.colorTiers.total = colorTiers.selectedOption.value
    if (colorTiers.enableCustomConfigs && colorTiers.customConfigs.length > 0) {
        colorTiers.customConfigs.forEach(colorTier => {
            if (configuration.colorTiers.tiers[colorTier.id]) {
                configuration.colorTiers.tiers[colorTier.id].active = true
                configuration.colorTiers.tiers[colorTier.id].value = colorTier.value
                configuration.colorTiers.tiers[colorTier.id].color = colorTier.color
            }
        })
    }

    return configuration
}

export const DEFAULT_PIE_CHART_CONFIG = {
    title: '',
    legendPosition: 'below',
    dataOverrides: {},
    dataTitle: {
        enabled: false,
        value: ''
    },
    format: 'Default',
    decimal: 0,
    prefix: '',
    suffix: '',
    alwaysShowLabels: false,
    labelDisplay: 'value'
}

export const getPieChartConfig = (chartConfig) => {
    const configuration = cloneDeep(DEFAULT_PIE_CHART_CONFIG)

    const basicFields = chartConfig.basicFields
    const dataFields = chartConfig.dataFields
    const scopeFields = chartConfig.scopeFields

    const chartTitle = basicFields.find(item => item.id === 'title')
    if (chartTitle) configuration.title = chartTitle.value

    const legend = basicFields.find(item => item.id === 'legend')
    if (legend && legend.selectedOption) configuration.legendPosition = legend.selectedOption.value

    const data = dataFields.find(item => item.id === 'data')
    if (data && data.enableCustomConfigs) {
        data.customConfigs.forEach(override => {
            configuration.dataOverrides[override.id] = override
        })
    }

    const enableDataTitle = dataFields.find(item => item.id === 'enableDataTitle')
    if (enableDataTitle && enableDataTitle.value) {
        configuration.dataTitle.enabled = enableDataTitle.value

        const dataTitle = dataFields.find(item => item.id === 'dataTitle')
        if (dataTitle) configuration.dataTitle.value = dataTitle.value
    }

    const format = dataFields.find(item => item.id === 'format')
    if (format && format.selectedOption) configuration.format = format.selectedOption.value

    const decimal = dataFields.find(item => item.id === 'decimal')
    if (decimal && decimal.selectedOption) configuration.decimal = decimal.selectedOption.value

    const prefix = dataFields.find(item => item.id === 'prefix')
    if (prefix) configuration.prefix = prefix.value

    const suffix = dataFields.find(item => item.id === 'suffix')
    if (suffix) configuration.suffix = suffix.value

    const alwaysShowLabels = dataFields.find(item => item.id === 'enableLabels')
    if (alwaysShowLabels && alwaysShowLabels.value) configuration.alwaysShowLabels = alwaysShowLabels.value

    const labelDisplay = dataFields.find(item => item.id === 'labelDisplay')
    if (labelDisplay && labelDisplay.selectedOption) configuration.labelDisplay = labelDisplay.selectedOption.value

    return configuration
}

export const getActualVisoDevices = (chart, dashboardDevices) => {
    const actualVisoDevices = []

    const chartVisoDevices = chart.configuration && chart.configuration.scopeFields?.find(pop => pop.id === 'devices').selectedOption

    if (chartVisoDevices && !isEmpty(chartVisoDevices)) {
        actualVisoDevices.push(...chartVisoDevices.map(device => device.value))
    } else if (dashboardDevices && !isEmpty(dashboardDevices)) {
        actualVisoDevices.push(...dashboardDevices)
    }

    return actualVisoDevices
}

export const getActualTimeRange = (chart, dashboardTimeRange, isPreview = false, isNewVersion = false, chartValues = {}) => {
    const actualTimeRange = {
        isDashboardTimeRange: false,
        start: '',
        end: ''
    }

    let chartTimeRange = {}

    if (isNewVersion) {
        // chartTimeRange = !isEmpty(chartValues) && chartValues?.dateRange && 
    } else {
        chartTimeRange = chart.configuration && chart.configuration.scopeFields?.find(pop => pop.id === 'dateRange').selectedOption
    }


    // const chartTimeRange = chart.configuration && chart.configuration.scopeFields?.find(pop => pop.id === 'dateRange').selectedOption

    if (!isPreview && (chartTimeRange && !isEmpty(chartTimeRange))) {
        actualTimeRange.isDashboardTimeRange = false

        if (isOptionRelativeTime(chartTimeRange)) {
            const { start, end } = getRelativeTimeDates(chartTimeRange)
            actualTimeRange.start = start
            actualTimeRange.end = end
        } else {
            actualTimeRange.start = chartTimeRange.start
            actualTimeRange.end = chartTimeRange.end
        }

    } else if (dashboardTimeRange && !isEmpty(dashboardTimeRange)) {
        actualTimeRange.isDashboardTimeRange = true

        if (isOptionRelativeTime(dashboardTimeRange)) {
            const { start, end } = getRelativeTimeDates(dashboardTimeRange)
            actualTimeRange.start = start
            actualTimeRange.end = end
        } else {
            actualTimeRange.start = dashboardTimeRange.start
            actualTimeRange.end = dashboardTimeRange.end
        }
    }

    return actualTimeRange
}

function hashStringToDelay(str, maxDelay = 1600) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i)
        hash |= 0
    }
    return Math.abs(hash % maxDelay);
}

export const fetchChartData = async (chart, visoDevices, timeRange) => {
    if (!timeRange?.start || !timeRange?.end) throw new Error('Error: No valid time range')


    return new Promise(async (resolve, reject) => {
        if (!chart || chart.metricColumns?.length === 0) {
            return reject(new Error('Error: No selected metrics'))
        }

        const delay = hashStringToDelay(chart.id, 400)
        await new Promise(res => setTimeout(res, delay))

        const callParams = {
            chartId: chart.id,
            visoDevices: visoDevices,
            query: {
                single: [],
                range: {
                    name: 'chartTimeRange',
                    value: {
                        lower: timeRange.start,
                        upper: timeRange.end
                    }
                },
            }
        }

        const { data } = await chartData(callParams);

        if (data.errors) {
            return reject(new Error('No data in selected time range'))
        }

        resolve(data.data.chartData)
    })
}

export const fetchPreviewChartData = (previewChart, previewParams, timeRange) => {
    return new Promise(async (resolve, reject) => {
        if (!previewChart || previewChart.metricColumns?.length === 0) {
            return reject(new Error('Choose a data source to see a preview'))
        }

        const callParams = {
            tableName: previewChart.tableName,
            metricColumns: previewChart.metricColumns,
            dataPoint: previewParams.dataPoint,
            metricAggregations: previewParams.metricAggregations,
            intervalOption: previewParams.intervalOption,
            intervalQuantity: previewParams.intervalQuantity,
            groupByColumns: previewParams.groupByColumns,
            visoDevices: previewParams.visoDevices,
            // query: timeRange.isDashboardTimeRange ? {
            //   single: [],
            //   range: {
            //     name: 'chartTimeRange',
            //     value: {
            //       lower: new Date(timeRange.start).toISOString(),
            //       upper: new Date(timeRange.end).toISOString()
            //     }
            //   }
            // } : null,
            startTime: timeRange.start,
            endTime: timeRange.end
        }

        const { data } = await chartDataPreview(callParams)

        if (data.errors) {
            return reject(new Error('No data in selected time range'))
        }

        resolve(data.data.previewData)
    })
}

export const chartGradientDefs = (id, color, gradientOpacity = 'default') => {
    const opacities = {
        'default': [.7, .3],
        'light': [.3, .02]
    }
    const opacity = opacities[gradientOpacity] || opacities.default

    return (
        <defs>
            <linearGradient id={`${id}Fill`} x1='0' y1='0' x2='0' y2='1'>
                <stop stopColor={color} stopOpacity={opacity[0]} />
                <stop offset='1' stopColor={color} stopOpacity={opacity[1]} />
            </linearGradient>
        </defs>
    )
}

export const legendContent = (props) => {
    const { payload, dataOverrides, align, chartConfig, groupColors, selectedLegends = [], onLegendClick } = props
    const isCompare = chartConfig?.dataStreamSingle?.breakdownType === 'compare_streams'
    const isByCategory = chartConfig?.dataStreamSingle?.breakdownType === 'by_category'
    // Treat "no group by" as the case where there is no non-time group-by metric.
    // Time is always present for time series charts and should not force legends
    // to show group values instead of metric names.
    const globalGroupByMetrics = Array.isArray(chartConfig?.groupBy?.metrics)
        ? chartConfig.groupBy.metrics
        : []
    const nonTimeGlobalGroupBy = globalGroupByMetrics.filter((m) => !/time/i.test(m))

    const singleGroupByMetrics = Array.isArray(chartConfig?.dataStreamSingle?.groupBy?.metrics)
        ? chartConfig.dataStreamSingle.groupBy.metrics
        : []
    const nonTimeSingleGroupBy = singleGroupByMetrics.filter((m) => !/time/i.test(m))

    const hasGroupByGlobal = nonTimeGlobalGroupBy.length > 0
    const hasSingleGroupBy = nonTimeSingleGroupBy.length > 0
    const hasGroupBy = hasGroupByGlobal || hasSingleGroupBy

    return (
        <ul className={align === 'right' ? 'chart-legend-layout-right' : 'chart-legend-layout-horizontal'}>
            {payload.map((entry, index) => {
                const iconType = entry.type || 'square'
                const iconClass = `chart-legend-element__icon-${iconType}`

                let legendLabel = ''

                if (isByCategory) {
                    legendLabel = entry?.payload?.metric || entry?.payload?.name || entry?.payload?.group || entry.value
                } else if (hasGroupBy) {
                    legendLabel = entry?.payload?.group || entry.value
                } else {
                    // No groupBy: prefer the stream's configured Label; fall back to
                    // the underlying metric key so overrides can still apply.
                    const streams = chartConfig?.dataStreams?.streams || chartConfig?.dataStreamSingle?.dataStreams?.streams || []
                    const stream = streams[index] || {}
                    const metrics = Array.isArray(stream?.metric?.metrics) ? stream.metric.metrics : []
                    const metricKey = metrics.length > 0
                        ? metrics[0]
                        : (entry?.payload?.metric || entry.value)
                    const streamLabel = stream?.streamLabel?.value
                    legendLabel = streamLabel || metricKey
                }
                
                const metricId = entry.id || entry?.payload?.metric || legendLabel
                const isSelected = selectedLegends && selectedLegends.length > 0 && selectedLegends.includes(metricId)
                const hasSelection = selectedLegends && selectedLegends.length

                if (isCompare) {
                    // Compare streams: use stream label color/text; fallback to alternating palette order
                    const stream = chartConfig?.dataStreams?.streams?.[index] || chartConfig?.dataStreamSingle?.dataStreams?.streams?.[index]
                    const streamColor = stream?.streamLabel?.color || getThemeThenRandomSeriesColor({
                        key: stream?.streamLabel?.value || stream?.name || index,
                        index,
                        palette: THEME_COLOR_SWATCH
                    })
                    return (
                        <li 
                            key={`item-${index}`} 
                            className={classNames('chart-legend-element', {
                                'chart-legend-element--selected': isSelected,
                                'chart-legend-element--has-selection': hasSelection && !isSelected
                            })}

                            style={{ 
                                cursor: 'pointer', 
                                userSelect: 'none',
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onLegendClick) {
                                    onLegendClick(metricId)
                                }
                            }}
                        >
                            <span
                                className={iconClass}
                                style={{
                                    background: streamColor,
                                    display: 'inline-block',
                                }}
                            />
                            {renderLegend(legendLabel, entry, dataOverrides)}
                        </li>
                    )
                }

                // By category: compute workspace color order here to avoid depending on payload color
                const categoryKey = entry?.payload?.metric || entry?.payload?.name || entry?.payload?.group || entry.value || index
                const color = groupColors?.length > 0 && entry?.payload?.group
                    ? groupColors[index]
                    : getThemeThenRandomSeriesColor({ key: categoryKey, index, palette: THEME_COLOR_SWATCH })
                const backgroundColor = entry.color?.startsWith('url') ? color : entry.color || color
                return (
                    <li 
                        key={`item-${index}`} 
                        className={classNames('chart-legend-element', {
                                'chart-legend-element--selected': isSelected,
                                'chart-legend-element--has-selection': hasSelection && !isSelected
                            })}
                        style={{ 
                            cursor: 'pointer', 
                            userSelect: 'none',
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (onLegendClick) {
                                onLegendClick(metricId)
                            }
                        }}
                    >
                        <span
                            className={iconClass}
                            style={{
                                background: backgroundColor,
                                display: 'inline-block',
                            }}
                        />
                        {renderLegend(legendLabel, entry, dataOverrides)}
                    </li>
                )
            })}
        </ul>
    )
}

export const renderLegend = (value, entry, dataOverrides) => {
    const payloadMetric = entry?.payload?.metric
    let label = value || payloadMetric || ''

    if (!isEmpty(dataOverrides)) {
        let overrideKey = null

        // Prefer using the legend value as the override key when it exists in
        // dataOverrides (e.g. underlying metric name), otherwise fall back to
        // the payload metric key (which may be an alias or group key).
        if (value && dataOverrides[value] !== undefined && dataOverrides[value] !== null) {
            overrideKey = value
        } else if (payloadMetric && dataOverrides[payloadMetric] !== undefined && dataOverrides[payloadMetric] !== null) {
            overrideKey = payloadMetric
        }

        if (overrideKey !== null) {
            const override = dataOverrides[overrideKey]
            const overrideValue = typeof override === 'object' && override.value !== undefined ? override.value : override

            // Only apply non-empty overrides; otherwise keep the original label.
            if (typeof overrideValue === 'string' ? overrideValue.trim().length > 0 : overrideValue != null) {
                label = overrideValue
            }
        }
    }

    return <span className='charts-no-cartesian__legend' title={label}>{truncate(label, 12)}</span>
}

const getOverrideData = (data, chartConfig) => {

    if (chartConfig?.colorTiers?.customConfigs?.length > 0 && chartConfig?.enableThreshold) {

        const tiers = chartConfig.colorTiers.customConfigs;
        let labelText = tiers[0].text;
        let configData = tiers[0];

        for (const tier of tiers) {
            if (data > parseFloat(tier.value)) {
                labelText = tier.text;
                configData = tier;
            }
        }
        return configData

    }

    return null

}

export const legendPayload = (chartData, dataOverrides = {}, chartConfig = {}) => {
    const safeData = Array.isArray(chartData) ? chartData : []

    // Detect synthetic preview datasets (e.g., gauge/doughnut % of max): they only
    // contain artificial keys like "value", "value11", and "remainder".
    const syntheticNames = ['value', 'value11', 'remainder']
    const allNames = safeData.map((entry) => {
        const name = (entry && (entry.metric || entry.name)) || ''
        return String(name).toLowerCase()
    })
    const isSyntheticPreviewSet = allNames.length > 0 && allNames.every((n) => syntheticNames.includes(n))

    // Filter out synthetic slices so they never appear as user-facing legend
    // series. For general data we always drop 'remainder'; for synthetic preview
    // sets we drop 'value'/'value11' as well.
    const filtered = safeData.filter((entry) => {
        const name = (entry && (entry.metric || entry.name)) || ''
        const lower = String(name).toLowerCase()

        if (lower === 'remainder') return false
        if (isSyntheticPreviewSet && (lower === 'value' || lower === 'value11')) return false
        return true
    })

    return filtered.map((entry, index) => {
        const isCompare = chartConfig?.dataStreamSingle?.breakdownType === 'compare_streams'
        if (isCompare) {
            const streams = chartConfig?.dataStreams?.streams || chartConfig?.dataStreamSingle?.dataStreams?.streams || []
            const stream = streams[index] || {}
            const metricKey = (Array.isArray(stream?.metric?.metrics) && stream.metric.metrics.length > 0)
                ? stream.metric.metrics.join(', ')
                : entry.metric
            return ({
                value: metricKey,
                type: 'square',
                id: metricKey,
                payload: { ...entry, metric: metricKey },
                color: stream?.streamLabel?.color || getThemeThenRandomSeriesColor({
                    key: stream?.streamLabel?.value || metricKey,
                    index,
                    palette: THEME_COLOR_SWATCH
                })
            })
        }

        // By category: fixed workspace color order 0, last, 1, last-1, 2, ...
        return ({
            value: entry.name || entry.metric || entry.value,
            type: 'square',
            id: entry.metric,
            payload: entry,
            color: getThemeThenRandomSeriesColor({
                key: entry.metric || entry.name || entry.value,
                index,
                palette: THEME_COLOR_SWATCH
            })
        })
    })
}

export const renderCartesianLegend = ({ uniqueGroupByValue = {}, metricColumns, chartConfig, uniqueGroupByOriginalKey, iconType, dataStreams }) => {
    // Build legend source array
    let legendItems = []

    // Primary: when dataStreams are provided (new v2 builder), use stream labels/colors
    const hasStreams = Array.isArray(dataStreams) && dataStreams.length > 0
    if (hasStreams) {
        const sortedArray = sortBy(dataStreams, 'name')
        legendItems = sortedArray
            .filter(item => !!item.streamLabel)
            .map((item, index) => {
                return ({
                    name: item.streamLabel?.value || `Stream ${index + 1}`,
                    color: item.streamLabel?.color || getThemeThenRandomSeriesColor({
                        key: item.streamLabel?.value || item.name || index,
                        index,
                        palette: THEME_COLOR_SWATCH
                    })
                })
            })
    } else if (Array.isArray(metricColumns) && metricColumns.length > 0) {
        // Fallback: derive from metric columns and overrides/colors in chartConfig
        legendItems = metricColumns.map((metric, index) => ({
            name: chartConfig?.yDataOverrides?.[metric] || metric,
            color: chartConfig?.yDataColorObj?.[metric] || getThemeThenRandomSeriesColor({
                key: metric,
                index,
                palette: THEME_COLOR_SWATCH
            })
        }))
    } else if (uniqueGroupByValue && Object.keys(uniqueGroupByValue).length > 0) {
        // Legacy/group-by fallback (rare in v2 raw preview)
        legendItems = Object.entries(uniqueGroupByValue).map(([key, value]) => ({
            name: uniqueGroupByOriginalKey?.[key] || key,
            color: value
        }))
    }

    return (
        legendItems.map((item, index) => (
            <div key={index} className='chart-legend-element'>
                <span
                    className={`chart-legend-element__icon-${iconType}`}
                    style={{ backgroundColor: item.color }}
                />
                <span
                    className='chart-legend-element__name'
                    title={item.name}
                    aria-label={item.name}
                >
                    {truncate(item.name, 9) || `Stream ${index + 1}`}
                </span>
            </div>
        ))
    )
}

export const GROUP_BY_UNIQUE_COLORS = [
    '#2184F9',
    '#845EC2',
    '#D65DB1',
    '#FF6F91',
    '#FF9671',
    '#80C07B',
    '#FF7A7A',
    '#007FFF',
    '#FFB347',
    '#FF3E96',
    '#5A20CB',
    '#00C49F',
    '#FF9F00',
    '#D93737',
    '#1E90FF',
    '#7B61FF',
    '#FFA07A',
    '#00FA9A',
    '#FF4500',
    '#9370DB'
]

export const formatToValidGroupByKey = (key) => {
    return key.replace(/\s+/g, '_').replace(/[^\w_]/g, '')
}

export const safeParseInt = (value) => {
    const parsed = parseInt(value)
    return isNaN(parsed) ? 0 : parsed
}

export const safeParseNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

export const getUniqueGroupByColors = (uniqueGroupValue, availableColorIndices, groupByValues, groupByOriginalKeys) => {
    let uniqueValue = ''
    let availableIndices = [...availableColorIndices]
    const uniqueValueColors = { ...groupByValues }
    const uniqueOriginalKeys = { ...groupByOriginalKeys }
    const uniqueOriginalValue = Object?.entries(uniqueGroupValue)
        .filter(([key, value]) => key !== 'time')
        .map(([key, value]) => value)[0]

    if (availableIndices.length === 0) {
        availableIndices = Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys())
    }

    if (uniqueOriginalValue && (typeof (uniqueOriginalValue) === 'string')) {
        uniqueValue = formatToValidGroupByKey(uniqueOriginalValue)
        uniqueOriginalKeys[uniqueOriginalValue] = uniqueValue
    }

    if (!uniqueValueColors.hasOwnProperty(`${uniqueValue}`)) {
        const randomColorIndex = Math.floor(Math.random() * availableIndices.length)
        const colorIndex = availableIndices.splice(randomColorIndex, 1)[0]
        uniqueValueColors[`${uniqueValue}`] = GROUP_BY_UNIQUE_COLORS[colorIndex]
    }
}

export const convertValuesToNumbers = (obj) => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = Number(value)
        return acc
    }, {})
}

export const removeUnusedMetrics = (data, metricsTableEnabled) => {
	if (metricsTableEnabled) {
		return data
	}
	const removeTables = ['cpu', 'disk', 'diskio', 'kernel', 'mem', 'processes', 'swap', 'system']
	return data.filter(set => !removeTables.includes(set.tableName))
}

export const filterSelectedMetrics = (tableName, data) => {
    return data.filter(set => set.tableName === tableName)
}

export const handleDeleteNullKeysChartData = (data) => {
    return data.map(element => {
        const newElement = { ...element }
        newElement.values = Object.keys(newElement.values).forEach(key => {
            if (item.values[key] === null) {
                delete item.values[key] // = "No Data"
            }
        });
        return newElement
    })
}

export const mapChartValuesToCartesianConfig = (inputConfig) => {
    const config = {
        title: inputConfig.chartTitle || "",
        legendPosition: inputConfig.legend || "below",
        chartLayout: inputConfig.chartLayout || "stacked",
        format: inputConfig.format || "Default",
        decimal: inputConfig.decimal || 0,
        yPrefix: inputConfig.prefix || "",
        ySuffix: inputConfig.suffix || "",
        yTitle: {
            enabled: inputConfig.enableYAxisTitle || false,
            value: inputConfig.yAxisTitle || ""
        },
        threshold: {
            enabled: inputConfig.enableThreshold || false,
            name: inputConfig.thresholdName || "",
            type: inputConfig.thresholdType || "",
            value: inputConfig.thresholdConfig?.value || 0,
            color: inputConfig.thresholdConfig?.color || ""
        },
        yDataColors: [],
        xTitle: {
            enabled: inputConfig.enableXAxisTitle || false,
            value: inputConfig.xAxisTitle || ""
        },
        xGroupByColors: (inputConfig.groupBy?.metrics || []).map(metric =>
            inputConfig.groupBy.metricConfig?.[metric]?.color || ""
        ).filter(color => color !== ""),
        dateFormatter: inputConfig.chartDateFormat || "dateFormatter",
        intervalQuantity: inputConfig.analysisInterval?.quantity || 0,
        yDataOverrides: {},
        yDataColorObj: {}
    };

    inputConfig.dataStreams.streams.forEach(stream => {
        const metricConf = stream.metric.metricConfig

        Object.entries(metricConf).forEach(([metric, conf]) => {
            config.yDataOverrides[metric] = conf.value
            config.yDataColorObj[metric] = conf.color
            config.yDataColors.push(conf.color)
        })
    })

    return config
}

export const metricConfigToLegend = (items, metricConfig) => {
    return items.map(item => {
        const detail = metricConfig[item.name] || {};
        return {
            ...item,
            name: detail.value || item.name,
            color: detail.color !== undefined ? detail.color : item.color
        }
    })
}

export const getColorFromMetricConfig = (metricConfig, metricName) => {
    const detail = metricConfig[metricName] || {};
    return detail.color !== undefined ? detail.color : "";
}

export const getChartRenderRequirements = (chartConfig, dateRange) => {
    const getMaxFieldsPerStream = () => {
        let maxFields = 0
        chartConfig?.dataStreams?.streams?.forEach((stream) => {
            const fieldCount = stream?.metric?.metrics?.length || 0
            if (fieldCount > maxFields) {
                maxFields = fieldCount
            }
        })
        return maxFields
    }

    const context = {
        interval: chartConfig?.analysisInterval,
        timeRange: dateRange,
        streamCount: chartConfig?.dataStreams?.value ?? 0,
        intensiveStreamCount: chartConfig?.dataStreams?.intensiveCount ?? 0,
        fieldsPerStream: getMaxFieldsPerStream(),
        // totalOptions: 0,
        // complexityScore: 0,
        groupByCount: chartConfig?.groupBy?.metrics?.length ?? 0
    }

    const result = {
        [BLOCK]: [],
        [WARN]: []
    }

    for (const rule of CHART_RENDER_RULESET) {
        if (rule.condition(context)) {
            result[rule.action].push({
                id: rule.id,
                action: rule.action,
                message: rule.message
            })
        }
    }

    return result
}

export const getDisplayedData = ({ chartData, chartConfig, value }) => {

    if (chartData?.data?.length === 0) {
        return 'No Data'
    }

    const format = chartConfig?.format ?? 'Default'
    const decimal = chartConfig?.decimal ?? 0
    const prefix = chartConfig?.prefix ?? ''
    const suffix = chartConfig?.suffix ?? ''
    const formatted = formatYAxisLabel(value, format, decimal)

    return `${prefix} ${formatted} ${suffix}`.trim()
}

// Temporary stub for filterChartDataByLegends - filter chart data by selected legends
export const filterChartDataByLegends = (chartData, selectedLegends) => {
    if (!selectedLegends || selectedLegends.length === 0) {
        return chartData;
    }

    return chartData.map(dataPoint => {
        const filteredPoint = {
            bucketed_time: dataPoint.bucketed_time,
            time: dataPoint.time,
            values: {}
        };

        // Only include properties that are in selectedLegends
        selectedLegends.forEach(legendName => {
            if (dataPoint.hasOwnProperty(legendName)) {
                filteredPoint[legendName] = dataPoint[legendName];
                filteredPoint.values[legendName] = dataPoint.values?.[legendName] || dataPoint[legendName];
            }
        });

        return filteredPoint;
    });
}

// Temporary stub for chartDotMenu - renders dot menu for chart
export const chartDotMenu = ({ color, onEdit, onExplainKPI, onImproveChart, onDelete, onCopy, showAI, isLight, isEditMode }) => {
    return {
        buttonProps: {
            iconSize: 'sm',
            color: isLight ? 'white-reserve' : color,
            style: isLight ? 'color' : 'plain'
        },
        closeOnClick: true,
        items: [
            ...(isEditMode ? [{ label: 'Edit chart', onClick: onEdit, iconLeft: 'edit' }] : []),
            ...(isEditMode ? [{ isDivider: true }] : []),
            ...(showAI ? [
                {
                    label: 'Explain my KPI',
                    onClick: onExplainKPI,
                    iconLeft: 'info',
                    badge: { color: color, text: 'AI' }
                },
                ...(isEditMode ? [{
                    label: 'Improve chart',
                    onClick: onImproveChart,
                    iconLeft: 'magic',
                    badge: { color: color, text: 'AI' }
                }] : []),

            ] : []),
            ...(isEditMode ? [{ label: 'Copy chart', onClick: onCopy, iconLeft: 'copy' }] : []),
            ...(isEditMode ? [{ label: 'Remove chart', onClick: onDelete, iconLeft: 'remove' }] : []),
        ]
    }
}

/**
 * Sanitize a string to be used as a gradient ID in SVG/DOM
 * Replaces invalid characters (parentheses, brackets, spaces, etc.) with underscores
 * @param {string} id - The ID string to sanitize
 * @returns {string} Sanitized ID safe for use in SVG gradient definitions
 */
export const sanitizeGradientId = (id) => {
    if (!id) return ''
    return id.replace(/[^a-zA-Z0-9_-]/g, '_')
}